import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaChevronDown, FaTimes } from 'react-icons/fa';
import './Header.scss';
import logoImage from '../../../assets/reddickrednobackground.png';
import baseLogo from '../../../assets/base-logo.svg';
import radixIcon from '../../../assets/radix-icon.svg';
import { BASE_BUY_URL, RSWAP_SWAP_URL, ASTROLESCENT_BRIDGE_URL } from '../../../config/siteContent';

const ABOUT_LINKS = [
  { type: 'route', to: '/tokenomics', label: 'Tokenomics' },
  { type: 'route', to: '/buy', label: 'Buy' },
];

const GET_IN_LINKS = [
  { type: 'route', to: '/stake', label: '$DCKS Staking', className: 'stake-link' },
  { type: 'route', to: '/validator', label: '$XRD Staking' },
];

const PLAY_LINKS = [
  { type: 'route', to: '/game-info', label: 'Reddicks Games' },
  { type: 'route', to: '/image-creator', label: 'AI Meme Creator' },
  { type: 'route', to: '/memecollection', label: 'Meme Collection' },
];

const MORE_LINKS = [
  {
    type: 'external',
    href: 'https://wallet.radixdlt.com/',
    label: 'Download Radix Wallet',
  },
];


const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [shellHeight, setShellHeight] = useState(88);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 860 : false
  );
  const shellRef = useRef(null);
  const navRef = useRef(null);

  const closeAllMenus = useCallback(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownKey) => {
    setOpenDropdown((prev) => (prev === dropdownKey ? null : dropdownKey));
  };

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!shellRef.current) {
      return undefined;
    }

    const updateShellHeight = () => {
      if (shellRef.current) {
        setShellHeight(shellRef.current.offsetHeight || 88);
      }
    };

    updateShellHeight();

    window.addEventListener('resize', updateShellHeight);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', updateShellHeight);
    }

    const observer = new ResizeObserver(updateShellHeight);
    observer.observe(shellRef.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateShellHeight);
    };
  }, [isScrolled]);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Track mobile breakpoint for portal rendering
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 860);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Also check navRef since the mobile nav may be portaled outside the shell
      if (
        shellRef.current &&
        !shellRef.current.contains(event.target) &&
        (!navRef.current || !navRef.current.contains(event.target))
      ) {
        closeAllMenus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeAllMenus]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        closeAllMenus();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [closeAllMenus]);

  useEffect(() => {
    if (!mobileMenuOpen || !navRef.current) return undefined;

    const focusable = Array.from(
      navRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.closest('[aria-hidden="true"]'));

    if (!focusable.length) return undefined;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first.focus();

    const trapFocus = (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', trapFocus);
    return () => document.removeEventListener('keydown', trapFocus);
  }, [mobileMenuOpen, openDropdown]);

  const isGroupActive = useCallback(
    (items) =>
      items.some((item) => item.type === 'route' && location.pathname.startsWith(item.to)),
    [location.pathname]
  );

  const renderDropdown = (dropdownKey, label, items, extraClass = '') => {
    const isOpen = openDropdown === dropdownKey;
    const activeClass = isGroupActive(items) ? 'active' : '';
    const triggerId = `${dropdownKey}-menu-trigger`;
    const menuId = `${dropdownKey}-menu`;

    return (
      <li className={`dropdown-container ${extraClass} ${isOpen ? 'is-open' : ''}`}>
        <button
          id={triggerId}
          type="button"
          className={`nav-item dropdown-toggle ${activeClass}`}
          onClick={() => toggleDropdown(dropdownKey)}
          aria-expanded={isOpen}
          aria-controls={menuId}
          aria-haspopup="true"
          aria-label={`${label} menu`}
        >
          <span>{label}</span>
          <FaChevronDown className="dropdown-icon" aria-hidden="true" />
        </button>
        <ul
          id={menuId}
          className={`dropdown-menu ${isOpen ? 'show' : ''}`}
          aria-labelledby={triggerId}
          aria-hidden={!isOpen}
        >
          {items.map((item) => (
            <li key={`${dropdownKey}-${item.label}`}>
              {item.type === 'route' ? (
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `dropdown-item ${item.className || ''} ${isActive ? 'active' : ''}`
                  }
                  onClick={closeAllMenus}
                >
                  {item.label}
                </NavLink>
              ) : (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  {item.label}
                </a>
              )}
            </li>
          ))}
        </ul>
      </li>
    );
  };

  return (
    <div
      className={`home-header-shell ${isScrolled ? 'scrolled' : ''}`}
      ref={shellRef}
      style={{ '--header-shell-height': `${shellHeight}px` }}
    >

      <header className="home-header">
        <div className="logo">
          <Link to="/" onClick={closeAllMenus}>
            <img src={logoImage} alt="Reddicks Logo" className="brand-logo-img" />
            <span className="brand-name">REDDICKS</span>
          </Link>
        </div>

        <div className="header-right">
          <button
            type="button"
            className="mobile-menu-toggle"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="main-site-nav"
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* On desktop, render nav inline. On mobile, portal it to body
              to avoid backdrop-filter containing block issues. */}
          {isMobile ? createPortal(
            <nav
              id="main-site-nav"
              ref={navRef}
              className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}
              aria-label="Main site navigation"
              style={{ '--header-shell-height': `${shellHeight}px` }}
            >
              <ul>
                <li>
                  <NavLink
                    to="/home"
                    className={({ isActive }) => `nav-item home-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/verify"
                    className={({ isActive }) => `nav-item verify-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Verify
                  </NavLink>
                </li>
                {renderDropdown('about', 'About', ABOUT_LINKS, 'about-dropdown')}
                {renderDropdown('get-in', 'Get In', GET_IN_LINKS, 'get-in-dropdown')}
                {renderDropdown('play', 'Play', PLAY_LINKS, 'play-dropdown')}

                <li className="dckslap-nav-item">
                  <NavLink
                    to="/dckslap"
                    className={({ isActive }) => `nav-item dckslap-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    DCKSlap <span className="new-badge">NEW</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/treasury"
                    className={({ isActive }) => `nav-item treasury-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Treasury
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/charity"
                    className={({ isActive }) => `nav-item charity-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Charity
                  </NavLink>
                </li>

                {renderDropdown('more', 'More', MORE_LINKS, 'more-dropdown')}
              </ul>
            </nav>,
            document.body
          ) : (
            <nav
              id="main-site-nav"
              ref={navRef}
              className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}
              aria-label="Main site navigation"
            >
              <ul>
                <li>
                  <NavLink
                    to="/home"
                    className={({ isActive }) => `nav-item home-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/verify"
                    className={({ isActive }) => `nav-item verify-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Verify
                  </NavLink>
                </li>
                {renderDropdown('about', 'About', ABOUT_LINKS, 'about-dropdown')}
                {renderDropdown('get-in', 'Get In', GET_IN_LINKS, 'get-in-dropdown')}
                {renderDropdown('play', 'Play', PLAY_LINKS, 'play-dropdown')}

                <li className="dckslap-nav-item">
                  <NavLink
                    to="/dckslap"
                    className={({ isActive }) => `nav-item dckslap-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    DCKSlap <span className="new-badge">NEW</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/treasury"
                    className={({ isActive }) => `nav-item treasury-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Treasury
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/charity"
                    className={({ isActive }) => `nav-item charity-link ${isActive ? 'active' : ''}`}
                    onClick={closeAllMenus}
                  >
                    Charity
                  </NavLink>
                </li>

                {renderDropdown('more', 'More', MORE_LINKS, 'more-dropdown')}
              </ul>
            </nav>
          )}
        </div>
      </header>

      <div className="header-trade-strip" aria-label="Trade DCKS">
        {/* Animated gradient line at top */}
        <div className="trade-strip-glow" aria-hidden="true" />

        <div className="trade-strip-inner">
          {/* ─── Base CTA ─── */}
          <a
            href={BASE_BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="trade-strip-cta trade-strip-cta--base"
          >
            <span className="trade-strip-cta__live" />
            <span className="trade-strip-cta__chain">
              <img src={baseLogo} alt="Base" className="trade-strip-cta__logo trade-strip-cta__logo--base" />
            </span>
            <span className="trade-strip-cta__action">Buy DCKS</span>
            <span className="trade-strip-cta__arrow">→</span>
          </a>

          {/* ─── Bridge Separator ─── */}
          <a
            href={ASTROLESCENT_BRIDGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="trade-strip-bridge"
          >
            <span className="trade-strip-bridge__icon">⇄</span>
            <span className="trade-strip-bridge__text">Bridge</span>
          </a>

          {/* ─── Radix CTA ─── */}
          <a
            href={RSWAP_SWAP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="trade-strip-cta trade-strip-cta--radix"
          >
            <span className="trade-strip-cta__live" />
            <span className="trade-strip-cta__chain">
              <img src={radixIcon} alt="Radix" className="trade-strip-cta__logo trade-strip-cta__logo--radix" />
            </span>
            <span className="trade-strip-cta__action">Buy DCKS on RSwap</span>
            <span className="trade-strip-cta__arrow">→</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Header;
